import * as React from "react";
import { ResponsiveContainer, Tooltip } from "recharts";
import { cn } from "../../lib/utils";

type ChartConfig = Record<string, { label: string; color: string }>;

interface ChartContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  config?: ChartConfig;
  children: React.ReactElement;
}

const ChartContainer = ({
  config = {},
  className,
  children,
  ...props
}: ChartContainerProps) => {
  return (
    <div
      className={cn("h-[200px] w-full", className)}
      {...props}
    >
      <ResponsiveContainer width="100%" height="100%">
        {children}
      </ResponsiveContainer>
    </div>
  );
};
ChartContainer.displayName = "ChartContainer";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ChartTooltip = (props: any) => <Tooltip {...props} />;
ChartTooltip.displayName = "ChartTooltip";

interface PayloadEntry {
  name: string;
  value: string | number;
  color: string;
}

interface ChartTooltipContentProps extends React.HTMLAttributes<HTMLDivElement> {
  active?: boolean;
  payload?: PayloadEntry[];
  label?: string;
}

const ChartTooltipContent = React.forwardRef<
  HTMLDivElement,
  ChartTooltipContentProps
>(({ active, payload, label, className, ...props }, ref) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      ref={ref}
      className={cn(
        "rounded-lg border border-border bg-background px-3 py-2 text-sm shadow-md",
        className
      )}
      {...props}
    >
      {label != null && (
        <div className="font-medium text-foreground mb-1">{label}</div>
      )}
      <div className="flex flex-col gap-1">
        {payload.map((entry, i) => (
          <div key={i} className="flex items-center gap-2">
            <span
              className="shrink-0 rounded-[2px] border h-2.5 w-2.5"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-muted-foreground">
              {entry.name}: {String(entry.value)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
});
ChartTooltipContent.displayName = "ChartTooltipContent";

export { ChartContainer, ChartTooltip, ChartTooltipContent };
export type { ChartConfig, ChartContainerProps, ChartTooltipContentProps, PayloadEntry };
